import { useEffect, useState } from 'react';
import { selectedLotStore, type LotSpec } from '../data/plots';

/**
 * Dev-only floating button (📱) that copies the current ngrok tunnel URL to
 * the clipboard so the developer can paste it on a phone. Lives outside the
 * mode-specific HUDs so it appears in both Cesium and Leaflet views.
 *
 * Visible only when:
 *   - `import.meta.env.DEV` is true (production builds never render it), and
 *   - `public/dev-tunnel.json` exists (written by scripts/start-tunnel.mjs
 *     when you run `npm run dev:tunnel`).
 */
export function PhoneUrlButton() {
  const [tunnelUrl, setTunnelUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedLot, setSelectedLot] = useState<LotSpec | null>(
    selectedLotStore.get(),
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    fetch('/dev-tunnel.json', { cache: 'no-store' })
      .then((r) => {
        const ct = r.headers.get('content-type') || '';
        if (!r.ok || !ct.includes('json')) return null;
        return r.json();
      })
      .then((data: { url?: string } | null) => {
        if (!cancelled && data?.url) setTunnelUrl(data.url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unsub = selectedLotStore.subscribe(setSelectedLot);
    return () => {
      unsub();
    };
  }, []);

  // Riding 52 px above the share button (which itself hugs whatever's at the
  // bottom of the screen). We mirror the share button's positioning logic so
  // this stays aligned when LotDetails docks at the bottom.
  const [bottom, setBottom] = useState(122);
  useEffect(() => {
    const targetId = selectedLot ? 'lot-details-panel' : 'manglillo-footer';
    const recalc = () => {
      const el = document.getElementById(targetId);
      if (!el) {
        setBottom(122);
        return;
      }
      const GAP_ABOVE_BOTTOM = 12;
      const SHARE_HEIGHT = 40;
      const GAP_ABOVE_SHARE = 12;
      setBottom(
        window.innerHeight - el.getBoundingClientRect().top +
          GAP_ABOVE_BOTTOM + SHARE_HEIGHT + GAP_ABOVE_SHARE,
      );
    };
    const raf = requestAnimationFrame(recalc);
    const el = document.getElementById(targetId);
    const ro = el ? new ResizeObserver(recalc) : null;
    if (el && ro) ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [selectedLot, tunnelUrl]);

  if (!tunnelUrl) return null;

  const copyPhoneUrl = async () => {
    const target = new URL(
      window.location.pathname + window.location.search,
      tunnelUrl,
    ).toString();
    try {
      await navigator.clipboard.writeText(target);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Phone URL:', target);
    }
  };

  return (
    <button
      onClick={copyPhoneUrl}
      aria-label="Copy phone URL"
      title={copied ? 'Copied' : `Copy phone URL: ${tunnelUrl}`}
      style={{
        position: 'absolute',
        bottom,
        right: 16,
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(0,0,0,0.62)',
        color: 'white',
        cursor: 'pointer',
        fontSize: 18,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        padding: 0,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        transition: 'bottom 0.2s ease',
        zIndex: 6,
      }}
    >
      {copied ? '✓' : '📱'}
    </button>
  );
}
